<template>
  <template v-if="isLoggedIn">
    <div :class="containerClass" @click="onWrapperClick">
      <div class="layout-sidebar" @click="onSidebarClick">
        <div class="sidebarBrand">
          <img src="./assets/iroquoise_logo_rvb.png" alt="Iroquoise" class="sidebarLogo" />
        </div>
        <AppMenu :model="menu" />
      </div>

      <div class="mobileMenu">
          <i class="pi pi-home" @click="goMobileMenu('/')"></i>
          <i class="pi pi-users" @click="goMobileMenu('/clients')"></i>
          <i class="pi pi-calendar" @click="goMobileMenu('/agenda')"></i>
          <i class="pi pi-money-bill" @click="goMobileMenu('/price')"></i>
          <i class="pi pi-chart-line" @click="goMobileMenu('/reporting')"></i>
          <i class="pi pi-calendar-plus" @click="goMobileMenu('/planning')"></i>
          <i class="pi pi-bell" @click="goMobileMenu('/alerts')"></i>
          <i class="pi pi-cog" @click="goMobileMenu('/settings')"></i>
      </div>

      <div class="layout-main-container" :class="{ 'agenda-mobile-layout': isAgendaMobileLayout }">
        <div class="managerHeader" v-if="showGlobalSearch">
          <div class="globalQuickSearch">
            <i class="pi pi-search searchLeadIcon"></i>
            <AutoComplete
              ref="globalSearchRef"
              v-model="selectedClient"
              :suggestions="filteredClients"
              :loading="clientsLoading"
              @item-select="openClient"
              @complete="searchClient($event)"
              forceSelection
              autoHighlight
              field="displayLabel"
              appendTo="self"
              placeholder="Recherche client (nom ou téléphone)"
              inputStyle="width: 100%"
              class="globalQuickSearchInput"
            />
            <InputButton
              v-if="selectedClient"
              icon="pi pi-times"
              class="quickSearchClear p-button-text p-button-rounded p-button-sm"
              @click="clearSearch"
            />
          </div>
        </div>
        <div class="layout-main">
          <router-view :key="$route.fullPath" />
        </div>
        <div class="versionTag">v2026.04.04</div>
      </div>
    </div>
  </template>
  <template v-else>
    <router-view />
  </template>
</template>


<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import AppMenu from './AppMenu.vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { authHeader } from './helpers/authHeader.js';
import { useMainStore } from './store';






export default({
  components: {
    AppMenu
  },
  setup() {

const router = useRouter();
const route = useRoute();
const store = useMainStore();
const isLoggedIn = ref(false);
const clients = ref([]);
const filteredClients = ref([]);
const selectedClient = ref(null);
const globalSearchRef = ref(null);
const clientsLoading = ref(false);
const isMobileViewport = ref(false);
let keydownHandler = null;
let resizeHandler = null;

onMounted(() => {
syncAuthState();
keyDownHandler();
updateViewportState();
resizeHandler = () => updateViewportState();
window.addEventListener('resize', resizeHandler, { passive: true });
loadClients();
loadAlertCount();
});

watch(() => route.path, () => {
  syncAuthState();
  loadClients();
});

onBeforeUnmount(() => {
  if (keydownHandler) {
    document.removeEventListener('keydown', keydownHandler);
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
  }
});

function updateViewportState() {
  isMobileViewport.value = window.matchMedia('(max-width: 991px)').matches;
}

function syncAuthState() {
  isLoggedIn.value = !!localStorage.getItem('user');
}

const isAgendaRoute = computed(() => route.path.startsWith('/agenda'));
const isAgendaMobileLayout = computed(() => isMobileViewport.value && isAgendaRoute.value);
const showGlobalSearch = computed(() => !isAgendaMobileLayout.value);

function keyDownHandler() {
  keydownHandler = function (e) {
  const key = (e.key || '').toLowerCase();
  const target = e.target;
  const isEditable = !!target && (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable
  );

  if ((e.ctrlKey || e.metaKey) && key === 'k') {
    e.preventDefault();
    router.push('/');
    return;
  }

  if ((e.ctrlKey || e.metaKey) && key === 'f') {
    e.preventDefault();
    focusGlobalSearch();
    return;
  }

  if (!e.ctrlKey && !e.metaKey && key === '/' && !isEditable) {
    e.preventDefault();
    focusGlobalSearch();
  }
};
  document.addEventListener('keydown', keydownHandler);
}

function onWrapperClick() {
  // Intentionally left blank; keeps layout click handler stable.
}

function onSidebarClick() {
  // Intentionally left blank; keeps layout click handler stable.
}

function goMobileMenu(route){
  router.push(route);
}

function clearSearch() {
  selectedClient.value = null;
  filteredClients.value = [];
}

function focusGlobalSearch() {
  if (!globalSearchRef.value) {
    return;
  }

  if (typeof globalSearchRef.value.focus === 'function') {
    globalSearchRef.value.focus();
    return;
  }

  const input = globalSearchRef.value.$el?.querySelector?.('input');
  if (input && typeof input.focus === 'function') {
    input.focus();
  }
}

function normalizeText(text) {
  return (text || "")
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizePhone(text) {
  return (text || "").replace(/\D/g, "");
}

function phoneMatches(phone, queryDigits) {
  const phoneDigits = normalizePhone(phone);
  if (phoneDigits.includes(queryDigits)) {
    return true;
  }

  if (phoneDigits.startsWith("41")) {
    const localDigits = "0" + phoneDigits.substring(2);
    if (localDigits.includes(queryDigits)) {
      return true;
    }
  }

  if (phoneDigits.startsWith("0")) {
    const intlDigits = "41" + phoneDigits.substring(1);
    if (intlDigits.includes(queryDigits)) {
      return true;
    }
  }

  return false;
}

function loadClients() {
  const options = {
    "headers": authHeader()
  };

  clientsLoading.value = true;
  axios
    .get(`${process.env.VUE_APP_API_URL}/Clients`, options)
    .then(response => {
      clients.value = (response.data || []).map(client => ({
        ...client,
        displayLabel: client.name
      }));
      clientsLoading.value = false;
    })
    .catch(() => {
      clients.value = [];
      clientsLoading.value = false;
    });
}

function searchClient(event) {
  const query = event.query ? event.query.trim() : "";
  const queryName = normalizeText(query);
  const queryDigits = normalizePhone(query);
  const isPhoneSearch = queryDigits.length >= 3;

  if (!query.length) {
    filteredClients.value = clients.value.map(client => ({
      ...client,
      displayLabel: client.name
    }));
    return;
  }

  filteredClients.value = clients.value
    .filter((client) => {
      const nameMatch = normalizeText(client.name).includes(queryName);
      const phoneMatch = queryDigits.length > 0 && phoneMatches(client.phone, queryDigits);
      return nameMatch || phoneMatch;
    })
    .map(client => ({
      ...client,
      displayLabel: isPhoneSearch ? `${client.name} (${client.phone})` : client.name
    }));
}

function loadAlertCount() {
  const options = { "headers": authHeader() };
  axios
    .get(`${process.env.VUE_APP_API_URL}/Appointments/getFutureOutsideOpenings`, options)
    .then(response => {
      const alertCount = (response.data || []).length;
      store.$patch((state) => { state.alertCount = alertCount; });
    })
    .catch(() => {});
}

function openClient(event) {
  const client = event?.value || selectedClient.value;
  if (!client || !client.clientid) {
    return;
  }
  store.$patch((state) => {
    state.currentClientFile = client.clientid;
  });
  router.push({ path: '/clients' });
}



    const menu = ref([
                {
                    items: [{label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/'},
                    {label: 'Clients', icon: 'pi pi-fw pi-users', to: '/clients'},
                    {label: 'Agenda', icon: 'pi pi-fw pi-calendar', to: '/agenda'},
                    {label: 'Prix', icon : 'pi pi-fw pi-money-bill', to: '/price'},
                    {label: 'Rapports', icon: 'pi pi-fw pi-chart-line', to: '/reporting'},
                    {label: 'Planning', icon: 'pi pi-fw pi-calendar-plus', to: '/planning'},
                    {label: 'Alertes', icon: 'pi pi-fw pi-bell', to: '/alerts', badge: () => store.alertCount},
                    {label: 'Réglages', icon: 'pi pi-fw pi-cog', to: '/settings'}
                    ]
                }
        ]);


    const containerClass = computed(() => {
      return ['layout-wrapper', {
                'layout-static': 'static',
                'layout-mobile-sidebar-active': false,
				'p-input-filled': 'filled',
				'p-ripple-disabled': false
    }]});



      const toggle = (event) => {
            menu.value.toggle(event);
        };
     

        return { menu, toggle, containerClass, goMobileMenu, selectedClient, filteredClients, clientsLoading, searchClient, openClient, clearSearch, globalSearchRef, onWrapperClick, onSidebarClick, isLoggedIn, showGlobalSearch, isAgendaMobileLayout }
  }
});




</script>

<style>
#app {
  font-family: "IBM Plex Sans", "Segoe UI", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #0b1220;
}

body.mobile-dialog-open {
  overflow: hidden;
  overscroll-behavior: none;
}

.managerHeader {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.layout-main-container,
.layout-main {
  overflow-x: clip;
}

.managerTitle {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.globalQuickSearch {
  position: relative;
  width: min(760px, 100%);
}

.globalQuickSearchInput {
  width: 100%;
}

.globalQuickSearchInput .p-inputtext {
  width: 100%;
  min-height: 48px;
  border-radius: 12px;
  border: 2px solid #c8d3e1;
  background: #f8fbff;
  box-shadow: none;
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 500;
  padding-left: 2.4rem;
  padding-right: 2.4rem;
}

.globalQuickSearchInput .p-inputtext:focus {
  border-color: #3b82f6;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
}

.searchLeadIcon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
  color: #64748b;
  font-size: 1rem;
}

.quickSearchClear {
  position: absolute !important;
  right: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  color: #475569 !important;
}

.sidebarBrand {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 8px 8px 8px;
}

.sidebarLogo {
  width: 100%;
  max-width: 220px;
  height: auto;
}

.versionTag {
  position: fixed;
  bottom: 4px;
  right: 6px;
  font-size: 0.65rem;
  color: #b0b8c8;
  pointer-events: none;
  z-index: 0;
}

@media (max-width: 991px) {
  .sidebarLogo {
    max-width: 120px;
  }

  .globalQuickSearch {
    width: 100%;
  }

  .globalQuickSearchInput .p-inputtext {
    min-height: 44px;
    font-size: 0.98rem;
  }

  .globalQuickSearchInput :deep(.p-autocomplete-panel) {
    width: 100% !important;
    max-width: calc(100vw - 1rem) !important;
    box-sizing: border-box;
  }

  .globalQuickSearchInput :deep(.p-autocomplete-items) {
    max-width: 100%;
  }

  .globalQuickSearchInput :deep(.p-autocomplete-item) {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .layout-main-container.agenda-mobile-layout {
    padding-top: 0.3rem;
    padding-left: 0;
    padding-right: 0;
  }

  .layout-main-container.agenda-mobile-layout .layout-main {
    padding: 0;
  }

  .layout-main-container.agenda-mobile-layout .layout-main h1 {
    margin: 0.25rem 0.75rem 0.45rem;
  }

  .appointmentDialog .p-dialog-footer {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    justify-content: stretch !important;
    gap: 8px !important;
  }

  .appointmentDialog .p-dialog-footer > * {
    width: 100% !important;
    margin: 0 !important;
  }

  .appointmentDialog .p-dialog-footer .p-button {
    width: 100% !important;
    min-height: 34px !important;
    padding: 0.35rem 0.55rem !important;
    font-size: 0.9rem !important;
    justify-content: center !important;
    margin: 0 !important;
  }

  .appointmentDialog .p-dialog-footer .p-button .p-button-label {
    white-space: nowrap !important;
  }
}
</style>
